import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const GrammarPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [name, setName] = useState('');
  const [rule, setRule] = useState('');
  const [description, setDescription] = useState('');
  const [examples, setExamples] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/grammar-rules`, { withCredentials: true });
      setRules(res.data);
    } catch { toast.error('Failed to load grammar rules'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setName(''); setRule(''); setDescription(''); setExamples(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await axios.post(`${API}/conlangs/${conlangId}/grammar-rules`, {
        name, rule,
        description: description || null,
        examples: examples ? examples.split('\n').filter(s => s.trim()) : [],
      }, { withCredentials: true });
      setRules([...rules, res.data]);
      setDialogOpen(false);
      resetForm();
      toast.success('Grammar rule added');
    } catch { toast.error('Failed to add rule'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grammar rule?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/grammar-rules/${id}`, { withCredentials: true });
      setRules(rules.filter(r => r.rule_id !== id));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="grammar-title">Grammar</h2>
          <p className="text-foreground-muted mt-1">Define sentence patterns and grammar rules for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90" data-testid="add-grammar-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Rule
        </Button>
        )}
      </div>

      {rules.length > 0 ? (
        <div className="space-y-3" data-testid="grammar-rules-list">
          {rules.map(r => (
            <Card key={r.rule_id} className="bg-background-paper border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-lg text-foreground font-semibold">{r.name}</h3>
                    <p className="font-mono text-sm text-primary mt-1">{r.rule}</p>
                    {r.description && <p className="text-foreground-muted text-sm mt-2">{r.description}</p>}
                    {r.examples?.length > 0 && (
                      <div className="mt-2 text-xs text-foreground-subtle italic">
                        {r.examples.map((ex, i) => <div key={i}>{ex}</div>)}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(r.rule_id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No grammar rules yet</h3>
            <p className="text-foreground-muted mb-4">Define sentence structure patterns using phrase structure rules.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Add Rule</Button>
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div className="text-secondary text-sm">
          <strong>Grammar rules</strong> define how phrases and sentences are constructed. Use notation like "S &rarr; NP VP" (a sentence is a noun phrase followed by a verb phrase) or "NP &rarr; Det N Adj" to describe your language's patterns.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="grammar-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Grammar Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Sentence, Noun Phrase, Verb Phrase" className="bg-background border-border" required data-testid="grammar-name" />
            </div>
            <div className="space-y-2">
              <Label>Rule Pattern</Label>
              <Input value={rule} onChange={(e) => setRule(e.target.value)} placeholder="e.g., S → NP VP, NP → Det N (Adj)" className="bg-background border-border font-mono" required data-testid="grammar-rule" />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Explain when and how this rule applies..." className="bg-background border-border min-h-[80px]" data-testid="grammar-description" />
            </div>
            <div className="space-y-2">
              <Label>Examples (one per line)</Label>
              <Textarea value={examples} onChange={(e) => setExamples(e.target.value)} placeholder="talo katal mira = the person sees the ocean" className="bg-background border-border min-h-[60px] font-mono text-sm" data-testid="grammar-examples" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !name || !rule} data-testid="submit-grammar">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GrammarPage;
